// LIVE-DESKTOP (docs/design/2026-11-03-live-desktop.md §1-2): the Workspace as a desk. Pure: a
// window is a block plus geometry in units of a 24 x 16 grid of the surface (so the desk scales
// with the viewport), a z-order and a state. New windows take the largest free rectangle at their
// kind's default size; with no room they cascade from the front window; past five open, the
// least recently touched unpinned window docks itself. Layouts (tile, focus, cascade, stack) move
// only open, unpinned windows. Place names ("top left", "docked") let the agent say where a
// window is. The person owns geometry; the agent owns what is on the desk.
import type { CanvasBlockKind } from "./canvas-state";

export const COLS = 24;
export const ROWS = 16;
/** More open windows than this and the least recently touched one docks itself. */
export const MAX_OPEN = 5;
/** A window the person closed this recently is not reopened by a reload or a poll. */
export const CLOSED_KEEP_MS = 10 * 60 * 1000;
export const MIN_W = 4;
export const MIN_H = 3;

export type WinState = "open" | "docked" | "max" | "pinned";
export type Win = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  state: WinState;
  /** When it was placed (ms). */
  opened: number;
  /** The person's last move, resize or front on it (ms; 0: never). */
  touched: number;
};
export type Desk = { wins: Record<string, Win>; closed: Record<string, number> };
export type Rect = { x: number; y: number; w: number; h: number };
export type DeskLayout = "tile" | "focus" | "cascade" | "stack";

export const emptyDesk = (): Desk => ({ wins: {}, closed: {} });

const DEFAULT_SIZE: Record<CanvasBlockKind, [number, number]> = {
  board: [12, 8],
  calendar: [12, 8],
  doc: [8, 10],
  chain: [12, 5],
  video: [10, 6],
  image_grid: [10, 6],
  image: [6, 6],
  kpi: [5, 3],
  transcript: [6, 10],
};

export const defaultSize = (kind: CanvasBlockKind): { w: number; h: number } => {
  const [w, h] = DEFAULT_SIZE[kind] ?? [8, 6];
  return { w, h };
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** A rect kept on the grid and at least the minimum size. */
export function clampRect(r: Rect): Rect {
  const w = clamp(Math.round(r.w), MIN_W, COLS);
  const h = clamp(Math.round(r.h), MIN_H, ROWS);
  return { x: clamp(Math.round(r.x), 0, COLS - w), y: clamp(Math.round(r.y), 0, ROWS - h), w, h };
}

/** On the surface: open, maximised or pinned (not docked). */
export const isShown = (w: Win) => w.state !== "docked";

const shown = (d: Desk) => Object.values(d.wins).filter(isShown);
const topZ = (d: Desk) => Object.values(d.wins).reduce((m, w) => Math.max(m, w.z), 0);

/** The surface's cells taken by shown windows (a maximised one covers nothing: it is an overlay). */
function occupancy(wins: readonly Win[]): boolean[][] {
  const g = Array.from({ length: ROWS }, () => Array<boolean>(COLS).fill(false));
  for (const w of wins) {
    if (w.state === "max") continue;
    for (let y = w.y; y < Math.min(ROWS, w.y + w.h); y++)
      for (let x = w.x; x < Math.min(COLS, w.x + w.w); x++) {
        const row = g[y];
        if (row) row[x] = true;
      }
  }
  return g;
}

const fits = (g: boolean[][], x: number, y: number, w: number, h: number) => {
  if (x + w > COLS || y + h > ROWS) return false;
  for (let j = y; j < y + h; j++) for (let i = x; i < x + w; i++) if (g[j]?.[i]) return false;
  return true;
};

/** The largest empty rectangle of the grid (top-left first on ties); null: the grid is full. */
export function largestFree(wins: readonly Win[]): Rect | null {
  const g = occupancy(wins);
  let best: Rect | null = null;
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++) {
      if (g[y]?.[x]) continue;
      let maxW = COLS - x;
      for (let h = 1; y + h <= ROWS; h++) {
        let w = 0;
        while (w < maxW && !g[y + h - 1]?.[x + w]) w++;
        if (w === 0) break;
        maxW = w;
        if (!best || w * h > best.w * best.h) best = { x, y, w, h };
      }
    }
  return best;
}

/**
 * Where a new window of `size` goes: the first free spot at full size (top-left first); else the
 * largest free rectangle when it is at least the minimum; else cascaded from the front window.
 */
export function findPlace(wins: readonly Win[], size: { w: number; h: number }): Rect {
  const g = occupancy(wins);
  for (let y = 0; y + size.h <= ROWS; y++)
    for (let x = 0; x + size.w <= COLS; x++)
      if (fits(g, x, y, size.w, size.h)) return { x, y, w: size.w, h: size.h };
  const free = largestFree(wins);
  if (free && free.w >= Math.min(size.w, 6) && free.h >= Math.min(size.h, 4))
    return { x: free.x, y: free.y, w: Math.min(size.w, free.w), h: Math.min(size.h, free.h) };
  const front = [...wins].filter((w) => w.state !== "max").sort((a, b) => b.z - a.z)[0];
  const x = front ? front.x + 1 : 0;
  const y = front ? front.y + 1 : 0;
  return clampRect({ x: x + size.w > COLS ? 0 : x, y: y + size.h > ROWS ? 0 : y, ...size });
}

/** The window the desk docks to make room: unpinned, least recently touched, then oldest. */
function dockCandidate(d: Desk, except: string): Win | undefined {
  return shown(d)
    .filter((w) => w.id !== except && w.state !== "pinned")
    .sort((a, b) => a.touched - b.touched || a.opened - b.opened)[0];
}

/** A new window for a block, placed; past MAX_OPEN the least recently touched one docks first. */
export function openWindow(d: Desk, id: string, kind: CanvasBlockKind, now: number): Desk {
  let wins = { ...d.wins };
  const closed = { ...d.closed };
  delete closed[id];
  if (shown({ wins, closed }).length >= MAX_OPEN) {
    const out = dockCandidate({ wins, closed }, id);
    if (out) wins = { ...wins, [out.id]: { ...out, state: "docked" } };
  }
  const r = findPlace(Object.values(wins).filter(isShown), defaultSize(kind));
  wins[id] = { id, ...r, z: topZ({ wins, closed }) + 1, state: "open", opened: now, touched: 0 };
  return { wins, closed };
}

const patch = (d: Desk, id: string, f: (w: Win) => Win): Desk => {
  const w = d.wins[id];
  return w ? { ...d, wins: { ...d.wins, [id]: f(w) } } : d;
};

/** Brought to the front (a docked window is restored to where it was). */
export function front(d: Desk, id: string, now?: number): Desk {
  const w = d.wins[id];
  if (!w) return d;
  const top = topZ(d);
  if (w.z === top && w.state !== "docked" && now === undefined) return d;
  return patch(d, id, (x) => ({
    ...x,
    z: x.z === top ? x.z : top + 1,
    state: x.state === "docked" ? "open" : x.state,
    ...(now !== undefined ? { touched: now } : {}),
  }));
}

export const dock = (d: Desk, id: string): Desk => patch(d, id, (w) => ({ ...w, state: "docked" }));

/** Every unpinned window docked (`tidy`), or the named ones. */
export function tidy(d: Desk, ids?: readonly string[]): Desk {
  const only = ids ? new Set(ids) : null;
  const wins = { ...d.wins };
  for (const w of Object.values(wins))
    if ((only ? only.has(w.id) : w.state !== "pinned") && w.state !== "docked")
      wins[w.id] = { ...w, state: "docked" };
  return { ...d, wins };
}

export const toggleMax = (d: Desk, id: string, now: number): Desk =>
  front(
    patch(d, id, (w) => ({ ...w, state: w.state === "max" ? "open" : "max", touched: now })),
    id,
  );

export const togglePin = (d: Desk, id: string, now: number): Desk =>
  patch(d, id, (w) => ({ ...w, state: w.state === "pinned" ? "open" : "pinned", touched: now }));

/** Closed by the person: gone from the desk, remembered so a reload does not bring it back. */
export function close(d: Desk, id: string, now: number): Desk {
  const wins = { ...d.wins };
  delete wins[id];
  return { wins, closed: { ...d.closed, [id]: now } };
}

/** Moved or resized by the person (units; kept on the grid). */
export const setRect = (d: Desk, id: string, r: Rect, now: number): Desk =>
  patch(d, id, (w) => ({ ...w, ...clampRect(r), touched: now }));

/** The rects of a layout for `n` windows (in order). */
export function layoutRects(layout: DeskLayout, n: number): Rect[] {
  if (n <= 0) return [];
  if (n === 1) return [{ x: 0, y: 0, w: COLS, h: ROWS }];
  switch (layout) {
    case "focus": {
      const rest = n - 1;
      const h = Math.floor(ROWS / rest);
      return [
        { x: 0, y: 0, w: 17, h: ROWS },
        ...Array.from({ length: rest }, (_, i) => ({
          x: 17,
          y: i * h,
          w: COLS - 17,
          h: i === rest - 1 ? ROWS - i * h : h,
        })),
      ];
    }
    case "cascade":
      return Array.from({ length: n }, (_, i) => {
        const step = Math.min(i, 6);
        return { x: step * 2, y: step, w: 14, h: 10 };
      });
    case "stack": {
      const h = Math.max(MIN_H, Math.floor(ROWS / n));
      return Array.from({ length: n }, (_, i) => ({
        x: 0,
        y: Math.min(i * h, ROWS - h),
        w: COLS,
        h: i === n - 1 && i * h + h < ROWS ? ROWS - i * h : h,
      }));
    }
    case "tile": {
      if (n === 2)
        return [
          { x: 0, y: 0, w: 12, h: ROWS },
          { x: 12, y: 0, w: 12, h: ROWS },
        ];
      if (n === 3)
        return [
          { x: 0, y: 0, w: 12, h: ROWS },
          { x: 12, y: 0, w: 12, h: 8 },
          { x: 12, y: 8, w: 12, h: 8 },
        ];
      if (n === 4)
        return [
          { x: 0, y: 0, w: 12, h: 8 },
          { x: 12, y: 0, w: 12, h: 8 },
          { x: 0, y: 8, w: 12, h: 8 },
          { x: 12, y: 8, w: 12, h: 8 },
        ];
      const rows = Math.ceil(n / 3);
      const h = Math.max(MIN_H, Math.floor(ROWS / rows));
      return Array.from({ length: n }, (_, i) => {
        const row = Math.floor(i / 3);
        const inRow = Math.min(3, n - row * 3);
        const w = Math.floor(COLS / inRow);
        const col = i % 3;
        return {
          x: col * w,
          y: Math.min(row * h, ROWS - h),
          w: col === inRow - 1 ? COLS - col * w : w,
          h: row === rows - 1 ? ROWS - row * h : h,
        };
      });
    }
  }
}

/** Tablet tiling: `n` windows in two columns (a lone last one takes the full row). */
export function twoColumnRects(n: number): Rect[] {
  if (n <= 0) return [];
  if (n === 1) return [{ x: 0, y: 0, w: COLS, h: ROWS }];
  const rows = Math.ceil(n / 2);
  const h = Math.max(MIN_H, Math.floor(ROWS / rows));
  const half = COLS / 2;
  return Array.from({ length: n }, (_, i) => {
    const row = Math.floor(i / 2);
    const last = i === n - 1 && n % 2 === 1;
    return {
      x: last ? 0 : (i % 2) * half,
      y: Math.min(row * h, ROWS - h),
      w: last ? COLS : half,
      h: row === rows - 1 ? ROWS - row * h : h,
    };
  });
}

/** `tile` for a tablet: the open, unpinned windows in two columns, front first. */
export function tileTwoColumns(d: Desk): Desk {
  const movable = shown(d)
    .filter((w) => w.state !== "pinned")
    .sort((a, b) => b.z - a.z);
  const rects = twoColumnRects(movable.length);
  const wins = { ...d.wins };
  movable.forEach((w, i) => {
    const r = rects[i];
    if (r) wins[w.id] = { ...w, ...r, state: "open" };
  });
  return { ...d, wins };
}

/** `grid` (the older name) is `tile`. */
export const deskLayoutOf = (l: string | null | undefined): DeskLayout =>
  l === "focus" || l === "cascade" || l === "stack" ? l : "tile";

/**
 * A layout applied to the open, unpinned windows: `order` first (in its order), then the rest
 * front first. Maximised windows return to open. Pinned and docked windows stay as they are.
 */
export function arrange(d: Desk, layout: DeskLayout, order: readonly string[] = []): Desk {
  const movable = shown(d).filter((w) => w.state !== "pinned");
  const rank = new Map(order.map((id, i) => [id, i]));
  const sorted = [...movable].sort(
    (a, b) =>
      (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER) ||
      b.z - a.z,
  );
  const rects = layoutRects(layout, sorted.length);
  const wins = { ...d.wins };
  let z = topZ(d);
  // The first in order ends on top.
  for (let i = sorted.length - 1; i >= 0; i--) {
    const w = sorted[i];
    const r = rects[i];
    if (!w || !r) continue;
    wins[w.id] = { ...w, ...r, state: "open", z: ++z };
  }
  return { ...d, wins };
}

/**
 * The desk kept in step with the canvas's blocks: windows of blocks no longer there are dropped;
 * a block without a window gets one (unless the person closed it lately); old closes expire.
 */
export function reconcile(
  d: Desk,
  blocks: readonly { id: string; kind: CanvasBlockKind }[],
  now: number,
): Desk {
  const ids = new Set(blocks.map((b) => b.id));
  let next: Desk = d;
  const gone = Object.keys(d.wins).filter((id) => !ids.has(id));
  const stale = Object.entries(d.closed).filter(
    ([id, at]) => !ids.has(id) || now - at > CLOSED_KEEP_MS,
  );
  if (gone.length || stale.length) {
    const wins = { ...d.wins };
    for (const id of gone) delete wins[id];
    const closed = { ...d.closed };
    for (const [id] of stale) delete closed[id];
    next = { wins, closed };
  }
  for (const b of blocks)
    if (!next.wins[b.id] && next.closed[b.id] === undefined)
      next = openWindow(next, b.id, b.kind, now);
  return next;
}

/** A window's geometry as the op log carries it (`place`, the folded `windows`). */
export type Placed = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z?: number | undefined;
  state?: WinState | undefined;
};

/**
 * The folded geometry from the server taken in for blocks this browser has no window for yet (a
 * docked window never placed, w = 0, gets its kind's default size for when it is restored).
 */
export function fromServer(
  d: Desk,
  windows: Record<string, Omit<Placed, "id">> | undefined,
  blocks: readonly { id: string; kind: CanvasBlockKind }[],
  now: number,
): Desk {
  if (!windows) return d;
  let next = d;
  for (const b of blocks) {
    const g = windows[b.id];
    if (!g || next.wins[b.id] || next.closed[b.id] !== undefined) continue;
    const r =
      g.w > 0 && g.h > 0
        ? clampRect(g)
        : findPlace(Object.values(next.wins).filter(isShown), defaultSize(b.kind));
    next = {
      ...next,
      wins: {
        ...next.wins,
        [b.id]: { id: b.id, ...r, z: g.z ?? 0, state: g.state ?? "open", opened: now, touched: 0 },
      },
    };
  }
  return next;
}

/** Geometry from a `place` op applied (an agent's never moves a pinned window). */
export function applyPlace(d: Desk, windows: readonly Placed[], o: { agent: boolean }): Desk {
  let next = d;
  for (const p of windows) {
    const w = next.wins[p.id];
    if (!w || (o.agent && w.state === "pinned")) continue;
    next = {
      ...next,
      wins: {
        ...next.wins,
        [p.id]: { ...w, ...clampRect(p), z: p.z ?? w.z, state: p.state ?? w.state },
      },
    };
  }
  return next;
}

/** The windows as a `place` op carries them. */
export const toPlaced = (w: Win): Placed => ({
  id: w.id,
  x: w.x,
  y: w.y,
  w: w.w,
  h: w.h,
  z: w.z,
  state: w.state,
});

/** A `place` op's identity without z (to know our own write when it comes back). */
export const placeSig = (windows: readonly Placed[]): string =>
  [...windows]
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .map((w) => `${w.id}:${w.x},${w.y},${w.w},${w.h},${w.state ?? ""}`)
    .join("|");

/** The windows in the order they were opened (keys 1-9). */
export const byOpened = (d: Desk): Win[] =>
  Object.values(d.wins).sort((a, b) => a.opened - b.opened);

/** The windows on the surface, back to front. */
export const surfaceOrder = (d: Desk): Win[] => shown(d).sort((a, b) => a.z - b.z);
/** The docked windows, in the order they were opened. */
export const docked = (d: Desk): Win[] =>
  Object.values(d.wins)
    .filter((w) => w.state === "docked")
    .sort((a, b) => a.opened - b.opened);
/** The front window (null: nothing on the surface). */
export const frontId = (d: Desk): string | null =>
  shown(d).sort((a, b) => b.z - a.z)[0]?.id ?? null;

/** Where a window is, in words ("top left", "right", "docked", "full screen"). */
export function placeName(w: Win): string {
  if (w.state === "docked") return "docked";
  if (w.state === "max") return "full screen";
  const cx = w.x + w.w / 2;
  const cy = w.y + w.h / 2;
  const col = cx < COLS / 3 ? "left" : cx > (COLS * 2) / 3 ? "right" : "";
  const row = cy < ROWS / 3 ? "top" : cy > (ROWS * 2) / 3 ? "bottom" : "";
  if (w.w >= COLS - 2 && w.h >= ROWS - 2) return "full screen";
  return [row, col].filter(Boolean).join(" ") || "centre";
}

const STORE_PREFIX = "cos.live-desk";
export const deskStoreKey = (companyId: string, sessionId: string) =>
  `${STORE_PREFIX}.${companyId}.${sessionId}`;

/** The desk kept for a session (empty when storage is blocked or holds something else). */
export function loadDesk(key: string): Desk {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return emptyDesk();
    const v = JSON.parse(raw) as Partial<Desk>;
    if (!v || typeof v !== "object" || typeof v.wins !== "object" || !v.wins) return emptyDesk();
    const wins: Record<string, Win> = {};
    for (const [id, w] of Object.entries(v.wins)) {
      if (!w || typeof w !== "object") continue;
      const state: WinState = ["open", "docked", "max", "pinned"].includes(w.state)
        ? w.state
        : "open";
      wins[id] = {
        id,
        ...clampRect(w),
        z: Number(w.z) || 0,
        state,
        opened: Number(w.opened) || 0,
        touched: Number(w.touched) || 0,
      };
    }
    const closed: Record<string, number> = {};
    for (const [id, at] of Object.entries(v.closed ?? {}))
      if (typeof at === "number") closed[id] = at;
    return { wins, closed };
  } catch {
    return emptyDesk();
  }
}

export function saveDesk(key: string, d: Desk): void {
  try {
    localStorage.setItem(key, JSON.stringify(d));
  } catch {
    // Private mode or full storage: the desk lives for this page only.
  }
}
